import React, { useEffect, useId, useRef, useState } from 'react';
import { useChatStrings } from '../../stringsContext';

interface CustomerVerificationCardProps {
   onSubmit: (username: string, roomNumber: string) => Promise<void>;
   isLoading: boolean;
   onClose: () => void;
}

/**
 * The verification dialog the API asks for before an action (name and room number, as on the booking).
 * On the tokens; opened only when the API demands it. Escape closes; focus starts in the first field.
 */
const CustomerVerificationCard: React.FC<CustomerVerificationCardProps> = ({ onSubmit, isLoading, onClose }) => {
   const strings = useChatStrings();
   const id = useId();
   const firstFieldRef = useRef<HTMLInputElement | null>(null);
   const [username, setUsername] = useState('');
   const [roomNumber, setRoomNumber] = useState('');
   const isValid = username.trim().length > 0 && roomNumber.trim().length > 0;

   useEffect(() => {
      firstFieldRef.current?.focus();
      const onKey = (e: KeyboardEvent) => {
         if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
   }, [onClose]);

   const handleSubmit = async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (isValid && !isLoading) await onSubmit(username.trim(), roomNumber.trim());
   };

   return (
      <div className="cx-scrim" onClick={onClose}>
         <form
            className="cx-dialog cx-frosted cx-frosted--thick"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${id}-title`}
            aria-describedby={`${id}-body`}
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
         >
            <h2 id={`${id}-title`} className="cx-dialog__title">
               {strings.verifyTitle}
            </h2>
            <p id={`${id}-body`} className="cx-dialog__body">
               {strings.verifyBody}
            </p>

            <div className="cx-field">
               <label htmlFor={`${id}-name`} className="cx-field__label">
                  {strings.verifyName}
               </label>
               <input
                  ref={firstFieldRef}
                  id={`${id}-name`}
                  className="cx-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={strings.verifyNamePlaceholder}
                  autoComplete="name"
                  dir="auto"
               />
            </div>

            <div className="cx-field">
               <label htmlFor={`${id}-room`} className="cx-field__label">
                  {strings.verifyRoom}
               </label>
               <input
                  id={`${id}-room`}
                  className="cx-input"
                  type="text"
                  inputMode="numeric"
                  value={roomNumber}
                  onChange={(e) => setRoomNumber(e.target.value)}
                  placeholder={strings.verifyRoomPlaceholder}
                  autoComplete="off"
               />
            </div>

            <div className="cx-dialog__actions">
               <button type="button" className="cx-btn" onClick={onClose}>
                  {strings.verifyCancel}
               </button>
               <button type="submit" className="cx-btn cx-btn--primary" disabled={!isValid || isLoading}>
                  {isLoading ? strings.verifyBusy : strings.verifyConfirm}
               </button>
            </div>

            <p className="cx-dialog__foot">{strings.verifyFoot}</p>
         </form>
      </div>
   );
};

export default CustomerVerificationCard;
